// Single source of truth for turning free-text courier event strings into
// a delivery category / 0-3 pipeline stage / on-hold flag.
//
// This used to be duplicated (and drift out of sync) across
// OrderTracking.js, ProgressTimeline.jsx, and OrderVerticalTimeline.jsx —
// see agent_docs/status-logic.md for the history. Read that doc before
// touching this file, adding a courier, or changing keyword rules.

const INDIA_POST_COURIER_ID = "9021";

// courierId === "9021" (India Post) uses a narrower, distinct keyword set.
// Every other courier ID falls back to the generic set below.
function classifyIndiaPostEvent(text) {
  // "not delivered" contains the substring " delivered", so the DELIVERED
  // check must explicitly exclude it — otherwise it wins on priority and
  // the "not delivered" -> IN TRANSIT branch below is unreachable dead
  // code (a real, pre-existing bug fixed here).
  //
  // "item delivery" (not "delivered") is a real India Post scrape variant
  // for the terminal delivered event, e.g. "Item Delivery Kodekal S.O
  // Kodekal S.O" — without it, that text falls through to the default
  // IN TRANSIT classification instead of DELIVERED.
  const isDelivered =
    text.includes("item delivered") ||
    text.includes("item delivery") ||
    text.includes("window delivery") ||
    text.includes(" delivered");
  if (isDelivered && !text.includes("not delivered")) return "DELIVERED";
  if (text.includes("hold") || text.includes("deposit")) return "HOLD";
  if (text.includes("out for delivery")) return "OUT FOR DELIVERY";
  if (text.includes("item booked")) return "BOOKED";
  if (text.includes("not delivered")) return "IN TRANSIT";
  if (text.includes("undelivered")) return "UNDELIVERED";
  return "IN TRANSIT";
}

function classifyGenericEvent(text) {
  // Same "not delivered" collision as classifyIndiaPostEvent, and via a
  // second path here too: text.includes("delivered") on its own also
  // matches "not delivered" (it isn't "undelivered", so that exclusion
  // doesn't catch it either).
  const isDelivered =
    text.includes("item delivered") ||
    text.includes(" delivered") ||
    (text.includes("delivered") && !text.includes("undelivered"));
  if (isDelivered && !text.includes("not delivered")) return "DELIVERED";
  if (text.includes("hold") || text.includes("deposit")) return "HOLD";
  if (
    text.includes("out for delivery") ||
    text.includes("shipment is out") ||
    text.includes("on fedex vehicle for delivery")
  )
    return "OUT FOR DELIVERY";
  if (
    text.includes("booking date") ||
    text.includes("article booked") ||
    text.includes("booked") ||
    text.includes("order confirmed") ||
    text.includes("picked up") ||
    text.includes("accepted") || 
    text.includes("out for pickup") || 
    text.includes("data received")
  )
    return "BOOKED";
  if (
    text.includes("not delivered") ||
    text.includes("in transit") ||
    text.includes("at destination")
  )
    return "IN TRANSIT";
  if (text.includes("undelivered")) return "UNDELIVERED";
  return "IN TRANSIT";
}

/**
 * Classifies a single event's free text into one of:
 * "BOOKED" | "IN TRANSIT" | "OUT FOR DELIVERY" | "DELIVERED" | "HOLD" | "UNDELIVERED"
 *
 * Courier-aware: dispatches to the India Post ruleset for courierId
 * "9021", the generic (wider) ruleset otherwise. The generic ruleset is
 * ProgressTimeline.jsx's former ruleset (a strict superset of
 * OrderTracking.js's former, narrower one) — consolidating onto it also
 * fixes the "known drift" agent_docs/status-logic.md used to document.
 *
 * "HOLD" is returned whenever the event text mentions hold/deposit,
 * regardless of recency — recency (whether a hold is still the *current*
 * state) is a separate, aggregate decision; see deriveOrderStatus.
 */
export function classifyEvent(eventText, courierId) {
  const text = (eventText || "").toLowerCase();
  return courierId === INDIA_POST_COURIER_ID
    ? classifyIndiaPostEvent(text)
    : classifyGenericEvent(text);
}

// How a single event's category affects the running 0-3 stage. Mirrors
// the former getStageFromEvents(IP) behavior: a monotonic max across the
// whole chronological event list, except HOLD hard-downgrades to 1
// (unless already delivered) since a paused delivery attempt means the
// item is no longer actively "out for delivery". IN TRANSIT/UNDELIVERED/
// anything unrecognized only ever raises the floor to 1, same as the
// pre-consolidation "else" catch-all.
function applyCategoryToStage(stage, category) {
  switch (category) {
    case "DELIVERED":
      return Math.max(stage, 3);
    case "OUT FOR DELIVERY":
      return Math.max(stage, 2);
    case "BOOKED":
      return Math.max(stage, 0);
    case "HOLD":
      return stage === 3 ? stage : 1;
    default: // IN TRANSIT, UNDELIVERED, or anything unrecognized
      return Math.max(stage, 1);
  }
}

/**
 * Derives the order's current pipeline stage and on-hold flag from its
 * full (chronologically sorted) event list, in one pass.
 *
 * `isOnHold` is recency-aware by construction: it's just "was the last
 * event's category HOLD", not "did any event ever mention hold/deposit".
 * Do not special-case "known resolving keywords" — any event after a HOLD
 * one, recognized or not, means renewed activity and clears the flag. An
 * earlier version whitelisted specific resolving keywords and left the
 * flag unchanged for anything else (e.g. "Item Invoiced"), which is what
 * caused it to go stale — see PR #57.
 */
export function deriveOrderStatus(events, courierId) {
  if (!events || events.length === 0) return { stage: 0, isOnHold: false };

  let stage = 0;
  let isOnHold = false;
  for (const e of events) {
    const category = classifyEvent(e.event, courierId);
    stage = applyCategoryToStage(stage, category);
    isOnHold = category === "HOLD";
  }
  return { stage, isOnHold };
}

/**
 * Whether the shipment is in the terminal "undeliverable" state — a
 * decision orthogonal to the 0-3 stage pipeline above (it's what
 * OrderTracking.js uses to pick the *Ups "undeliverable" components
 * instead of the normal ones). Not expanded beyond "disposed of" here;
 * broader RTO/"returned to sender" detection is a separate follow-up.
 */
export function isUndeliverableEvents(events) {
  return (events || []).some((e) =>
    (e.event || "").toLowerCase().includes("disposed of"),
  );
}
